/**
 * An order's history, in sentences.
 *
 * cg_activity has recorded fourteen kinds of event since the tracker was
 * built — who confirmed, what the client signed, which gate was overridden and
 * why, when a status was forced backwards — and not one screen in this product
 * has ever read it. The delete dialog says "The activity log keeps its
 * history"; that is copy about a log nobody could open.
 *
 * What it costs: every question that starts "when did…" or "who…" has been
 * answerable only by somebody with database access. Why did this order's
 * target date move? Who overrode the financial gate on it, and what reason did
 * they give? Did the client actually sign, or did we assume? All recorded, all
 * unreachable.
 *
 * ── Why the sentences live here ────────────────────────────────────────────
 *
 * `event` is free text with no CHECK constraint and `details` has a different
 * shape per event, so rendering needs a switch. Putting it in a pure module
 * means the wording has a test — and the wording is most of the value. A feed
 * saying "financial_gate_override" is a log; one saying "overrode the
 * financial gate — client paying on delivery, approved by Kyu" is an answer.
 */

export interface OrderActivityRow {
  id: string;
  event: string;
  details: Record<string, unknown> | null;
  actor_id: string | null;
  created_at: string;
}

/** A row reduced to what the feed renders. */
export interface ActivityLine {
  id: string;
  createdAt: string;
  /** Empty when the actor is the system or a client, who have no Hub account. */
  actorId: string;
  sentence: string;
  /** Set when the event deserves to stand out in a long list. */
  tone: 'neutral' | 'warning' | 'success';
}

function text(details: Record<string, unknown>, key: string): string {
  const value = details[key];
  return typeof value === 'string' ? value : '';
}

function count(details: Record<string, unknown>, key: string): number | null {
  const value = details[key];
  return typeof value === 'number' ? value : null;
}

/**
 * What one row says.
 *
 * Every branch is written from the reader's side: somebody opening an order
 * six weeks later who wants to know what happened to it, not somebody
 * debugging the trigger that wrote the row.
 *
 * The default deliberately does NOT hide an event it does not recognise.
 * `event` is free text and new ones arrive with new migrations; a feed that
 * silently drops what it has not been taught about is a feed that lies by
 * omission. De-underscoring is a poor sentence but an honest one.
 */
export function describeActivity(row: OrderActivityRow): ActivityLine {
  const details = row.details ?? {};
  return {
    id: row.id,
    createdAt: row.created_at,
    actorId: row.actor_id ?? '',
    ...sentenceFor(row.event, details),
  };
}

function sentenceFor(
  event: string,
  details: Record<string, unknown>,
): { sentence: string; tone: ActivityLine['tone'] } {
  switch (event) {
    case 'created': {
      const via = text(details, 'via');
      return {
        sentence:
          via === 'client_link'
            ? 'created this order through the client link wizard'
            : 'created this order',
        tone: 'neutral',
      };
    }
    case 'updated': {
      // `fields` is Object.keys(patch) — names only, no before/after. Listing
      // them is still worth more than "updated it": "changed target_ship_date"
      // answers most of the questions this feed gets asked.
      const fields = Array.isArray(details['fields'])
        ? details['fields'].filter((f): f is string => typeof f === 'string')
        : [];
      return {
        sentence: fields.length === 0 ? 'edited this order' : `changed ${fields.join(', ')}`,
        tone: 'neutral',
      };
    }
    case 'order_opened_from_sample':
      return { sentence: 'opened this order from a supplier sample', tone: 'neutral' };

    case 'client_signed_off': {
      const cert = text(details, 'certificate_no');
      const who = text(details, 'signed_by');
      const named = who === '' ? 'The client' : who;
      return {
        sentence:
          cert === ''
            ? `${named} signed off the order`
            : `${named} signed off the order — certificate ${cert}`,
        tone: 'success',
      };
    }

    case 'invoice_sent': {
      const on = text(details, 'invoice_sent_on');
      return {
        // Neutral, not success. Sending an invoice is not being paid, and a
        // green line for it is how a feed starts reading as money in.
        sentence: on === '' ? 'sent the client invoice' : `sent the client invoice on ${on}`,
        tone: 'neutral',
      };
    }
    case 'invoice_paid': {
      const on = text(details, 'date_paid');
      return {
        sentence: on === '' ? 'marked the invoice paid' : `marked the invoice paid on ${on}`,
        tone: 'success',
      };
    }
    case 'financial_gate_override': {
      // The single most important row this table holds. Somebody moved an
      // order past the money check; the reason they typed is the whole record
      // of that decision and it has never been visible anywhere.
      const reason = text(details, 'reason');
      const to = text(details, 'to_status');
      const where = to === '' ? '' : ` to ${to}`;
      return {
        sentence:
          reason === ''
            ? `overrode the financial gate${where}`
            : `overrode the financial gate${where} — ${reason}`,
        tone: 'warning',
      };
    }
    case 'po_sent_to_supplier': {
      const proofs = count(details, 'proof_count');
      return {
        sentence:
          proofs === null
            ? 'sent the PO to the supplier'
            : `sent the PO to the supplier with ${String(proofs)} approved proof${proofs === 1 ? '' : 's'}`,
        tone: 'neutral',
      };
    }

    case 'supplier_confirmed':
      return { sentence: 'the supplier accepted the order', tone: 'success' };
    case 'supplier_requested_revision': {
      const note = text(details, 'note');
      return {
        sentence:
          note === ''
            ? 'the supplier asked for a change to the PO'
            : `the supplier asked for a change to the PO — "${note}"`,
        tone: 'warning',
      };
    }
    case 'supplier_revision_acknowledged':
      return { sentence: "answered the supplier's request for a change", tone: 'neutral' };
    case 'supplier_ready_for_shipment': {
      const tracking = text(details, 'tracking');
      const courier = text(details, 'courier');
      const how = [courier, tracking].filter((part) => part !== '').join(' ');
      return {
        sentence:
          how === ''
            ? 'the supplier marked the order ready for collection'
            : `the supplier marked the order ready for collection — ${how}`,
        tone: 'success',
      };
    }
    case 'samples_approved':
      return { sentence: 'approved the pre-production samples', tone: 'success' };

    case 'status_moved_backwards': {
      const from = text(details, 'from');
      const to = text(details, 'to');
      return {
        sentence:
          from === '' || to === ''
            ? 'moved the order backwards through the pipeline'
            : `moved the order backwards, from ${from} to ${to}`,
        tone: 'warning',
      };
    }

    case 'supplier_linked': {
      const who = text(details, 'supplier');
      return {
        sentence: who === '' ? 'linked this order to a supplier' : `sent this order to ${who}`,
        tone: 'neutral',
      };
    }
    case 'supplier_changed': {
      // Both sides, because "why is this order with Factory B now" is the
      // question this row exists to answer.
      const to = text(details, 'supplier');
      const from = text(details, 'previous');
      return {
        sentence:
          from === '' || to === ''
            ? 'changed which supplier this order goes to'
            : `moved this order from ${from} to ${to}`,
        tone: 'warning',
      };
    }
    case 'po_issued':
      return { sentence: 'issued the PO', tone: 'neutral' };
    case 'po_revised': {
      // Names what changed. A revision voids the factory's acceptance, so six
      // weeks later "why did they have to re-accept" needs an answer here.
      const why = text(details, 'reason');
      const rev = count(details, 'revision');
      const which = rev === null ? 'the PO' : `PO revision ${String(rev)}`;
      return {
        sentence: why === '' ? `issued ${which}` : `issued ${which} — ${why}`,
        tone: 'warning',
      };
    }
    case 'review_link_reissued': {
      const replaced = count(details, 'replaced');
      return {
        sentence:
          replaced !== null && replaced > 0
            ? 'issued a new client review link, replacing the previous one'
            : 'issued a client review link',
        tone: 'neutral',
      };
    }
    case 'review_link_revoked': {
      const reason = text(details, 'reason');
      return {
        sentence:
          reason === ''
            ? 'revoked the client review link'
            : `revoked the client review link — ${reason}`,
        tone: 'warning',
      };
    }

    case 'order_deleted': {
      // Survives the order it describes: cg_activity.order_id is ON DELETE SET
      // NULL precisely so this row outlives the delete. It will not appear in
      // any order's feed, by definition — kept here so a future all-orders
      // view renders it rather than falling through to the underscore case.
      const reason = text(details, 'reason');
      // What went with it. Since 20261113000100 a delete also removes sample
      // runs holding no photos, and a row that disappeared without the log
      // saying so is the thing somebody spends an afternoon on six months
      // later. Runs WITH photos are left alone and are not mentioned here —
      // they still exist, so nothing happened to them worth reporting.
      // Null for a delete recorded before 20261113000100 added the field, and
      // that reads as "nothing to report" rather than as zero — which is the
      // same sentence either way, and the honest one for a delete that
      // genuinely swept nothing.
      const removed = count(details, 'empty_sample_runs_removed') ?? 0;
      const swept =
        removed <= 0
          ? ''
          : removed === 1
            ? ' — also removed 1 empty sample run'
            : ` — also removed ${String(removed)} empty sample runs`;
      const said = reason === '' ? '' : ` — ${reason}`;
      return {
        sentence: `deleted this order${said}${swept}`,
        tone: 'warning',
      };
    }

    default:
      return { sentence: event.replace(/_/g, ' '), tone: 'neutral' };
  }
}

/**
 * Newest first.
 *
 * The database index is on order_id, not on created_at, and the query orders
 * explicitly — but a feed that quietly depends on server ordering breaks the
 * day somebody adds a second source to it. Sorting here costs nothing on a
 * capped list and makes the guarantee local.
 */
export function sortActivity(lines: ActivityLine[]): ActivityLine[] {
  return [...lines].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}
